import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { AuthService } from 'src/app/services/auth/auth.service';
import { AdminSuscripcionesService } from 'src/app/services/admin-suscripciones/admin-suscripciones.service';

// Precios sugeridos por plan (editables por el usuario)
const PRECIOS: Record<string, Record<string, string>> = {
  MENSUAL: { MXN: '349', USD: '19', EUR: '17', COP: '75000', ARS: '18000', BRL: '95', CLP: '17000', PEN: '72' },
  ANUAL:   { MXN: '3490', USD: '190', EUR: '170', COP: '750000', ARS: '180000', BRL: '950', CLP: '170000', PEN: '720' },
};

@Component({
  selector: 'app-admin-suscripciones',
  templateUrl: './admin-suscripciones.component.html',
})
export class AdminSuscripcionesComponent implements OnInit {

  stats: any = { free: 0, premium: 0, cancelando: 0, canceled: 0, pastDue: 0, totalPagados: 0, ingresoTotal: 0 };

  pagos: any[] = [];
  totalPagos = 0;
  cargandoPagos = false;

  filtroNombre = '';
  filtroCorreo = '';
  filtroStatus = 'ALL';
  filtroMetodo = 'ALL';
  filtroDesde = '';
  filtroHasta = '';
  paginaActual = 0;
  readonly tamPagina = 50;

  // ── Formulario pago manual ─────────────────────────────────────────────────
  mostrarFormPago = false;
  guardandoPago   = false;
  errorPago       = '';

  // autocomplete
  busquedaTexto      = '';
  resultadosBusqueda: any[] = [];
  buscandoCandidatos = false;
  candidatoSeleccionado: any = null;
  private busqueda$ = new Subject<string>();

  // campos del formulario
  planTipo   = 'MENSUAL';
  monto      = '349';
  moneda     = 'MXN';
  metodo     = 'TRANSFERENCIA';
  notas      = '';
  comprobanteFile: File | null = null;
  comprobanteNombre = '';

  readonly monedas = ['MXN', 'USD', 'EUR', 'COP', 'ARS', 'BRL', 'CLP', 'PEN'];
  readonly metodos = [
    { value: 'TRANSFERENCIA', label: 'Transferencia bancaria' },
    { value: 'EFECTIVO',      label: 'Efectivo' },
    { value: 'STRIPE',        label: 'Stripe' },
    { value: 'OXXO',          label: 'OXXO' },
    { value: 'OTRO',          label: 'Otro' },
  ];

  // ── Detalle en tabla ───────────────────────────────────────────────────────
  pagoDetalle: any = null;

  constructor(
    private svc: AdminSuscripcionesService,
    private router: Router,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.cargarStats();
    this.cargarPagos();

    // Debounce de búsqueda de candidatos
    this.busqueda$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(q => {
        this.buscandoCandidatos = true;
        return this.svc.buscarCandidatos(q);
      })
    ).subscribe({
      next: (res) => {
        this.resultadosBusqueda = res?.data ?? [];
        this.buscandoCandidatos = false;
      },
      error: () => { this.buscandoCandidatos = false; }
    });
  }

  // ── Stats y pagos ──────────────────────────────────────────────────────────

  cargarStats(): void {
    this.svc.stats().subscribe({ next: (res) => { this.stats = res?.data ?? res; } });
  }

  cargarPagos(): void {
    this.cargandoPagos = true;
    const f: any = { page: this.paginaActual, size: this.tamPagina };
    if (this.filtroNombre.trim()) f.nombre = this.filtroNombre.trim();
    if (this.filtroCorreo.trim()) f.correo = this.filtroCorreo.trim();
    if (this.filtroStatus !== 'ALL') f.status = this.filtroStatus;
    if (this.filtroMetodo !== 'ALL') f.metodo = this.filtroMetodo;
    if (this.filtroDesde) f.desde = this.filtroDesde;
    if (this.filtroHasta) f.hasta = this.filtroHasta;

    this.svc.pagos(f).subscribe({
      next: (res) => {
        const d = res?.data ?? res;
        this.pagos = d?.content ?? [];
        this.totalPagos = d?.totalElements ?? this.pagos.length;
        this.cargandoPagos = false;
      },
      error: () => { this.cargandoPagos = false; }
    });
  }

  buscar(): void { this.paginaActual = 0; this.cargarPagos(); }

  limpiarFiltros(): void {
    this.filtroNombre = '';
    this.filtroCorreo = '';
    this.filtroStatus = 'ALL';
    this.filtroMetodo = 'ALL';
    this.filtroDesde  = '';
    this.filtroHasta  = '';
    this.paginaActual = 0;
    this.cargarPagos();
  }

  paginaAnterior(): void { if (this.paginaActual > 0) { this.paginaActual--; this.cargarPagos(); } }
  paginaSiguiente(): void {
    if ((this.paginaActual + 1) * this.tamPagina < this.totalPagos) { this.paginaActual++; this.cargarPagos(); }
  }

  // ── Autocomplete candidatos ────────────────────────────────────────────────

  onBusquedaCambio(texto: string): void {
    this.candidatoSeleccionado = null;
    if (texto.trim().length >= 2) {
      this.busqueda$.next(texto.trim());
    } else {
      this.resultadosBusqueda = [];
    }
  }

  seleccionarCandidato(c: any): void {
    this.candidatoSeleccionado = c;
    this.busquedaTexto         = `${c.nombre} — ${c.correo}`;
    this.resultadosBusqueda    = [];
  }

  // ── Plan y monto ───────────────────────────────────────────────────────────

  onPlanCambio(): void   { this.actualizarMonto(); }
  onMonedaCambio(): void { this.actualizarMonto(); }

  private actualizarMonto(): void {
    const precio = PRECIOS[this.planTipo]?.[this.moneda];
    if (precio) this.monto = precio;
  }

  // ── Comprobante ────────────────────────────────────────────────────────────

  onArchivoSeleccionado(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file  = input.files?.[0] ?? null;
    this.comprobanteFile   = file;
    this.comprobanteNombre = file?.name ?? '';
  }

  // ── Abrir / guardar / cerrar modal ────────────────────────────────────────

  abrirFormPago(): void {
    this.candidatoSeleccionado = null;
    this.busquedaTexto         = '';
    this.resultadosBusqueda    = [];
    this.planTipo              = 'MENSUAL';
    this.monto                 = '349';
    this.moneda                = 'MXN';
    this.metodo                = 'TRANSFERENCIA';
    this.notas                 = '';
    this.comprobanteFile       = null;
    this.comprobanteNombre     = '';
    this.errorPago             = '';
    this.mostrarFormPago       = true;
  }

  get formularioValido(): boolean {
    return !!this.candidatoSeleccionado && !!this.monto && parseFloat(this.monto) > 0;
  }

  guardarPagoManual(): void {
    if (!this.formularioValido) return;
    this.guardandoPago = true;
    this.errorPago     = '';

    const fd = new FormData();
    fd.append('candidatoId', this.candidatoSeleccionado.id.toString());
    fd.append('planTipo',    this.planTipo);
    fd.append('monto',       this.monto);
    fd.append('moneda',      this.moneda);
    fd.append('metodo',      this.metodo);
    if (this.notas?.trim()) fd.append('notas', this.notas.trim());
    if (this.comprobanteFile) fd.append('comprobante', this.comprobanteFile, this.comprobanteFile.name);

    this.svc.registrarPagoManual(fd).subscribe({
      next: () => {
        this.guardandoPago   = false;
        this.mostrarFormPago = false;
        this.cargarStats();
        this.cargarPagos();
      },
      error: (e) => {
        this.guardandoPago = false;
        this.errorPago = e?.error?.message ?? 'Error al registrar el pago. Intenta de nuevo.';
      }
    });
  }

  // ── Detalle ────────────────────────────────────────────────────────────────

  verDetalle(p: any): void  { this.pagoDetalle = p; }
  cerrarDetalle(): void { this.pagoDetalle = null; }

  // ── Helpers visuales ───────────────────────────────────────────────────────

  metodoBadgeColor(m: string): string {
    return ({ STRIPE: '#818cf8', TRANSFERENCIA: '#34d399', EFECTIVO: '#f59e0b', OXXO: '#fb923c', OTRO: '#94a3b8' } as any)[m] ?? '#94a3b8';
  }

  statusBadgeColor(s: string): string {
    return ({ PAGADO: '#4ade80', PENDIENTE: '#fb923c', FALLIDO: '#f87171' } as any)[s] ?? '#94a3b8';
  }

  planBadgeColor(p: string): string {
    return p === 'ANUAL' ? '#a78bfa' : '#60a5fa';
  }

  formatMonto(m: number, moneda: string): string {
    return `${moneda ?? ''} $${(m ?? 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }

  get paginaInicio(): number { return this.paginaActual * this.tamPagina + 1; }
  get paginaFin(): number    { return Math.min((this.paginaActual + 1) * this.tamPagina, this.totalPagos); }

  cerrarSesion(): void {
    this.authService.cerrarSesion();
    this.router.navigate(['/acceder']);
  }
}
